import { Dispatch, useState, createContext, SetStateAction, useEffect } from 'react'

import { Page, RegisterFields, RegisterErrors, SignUpScenario } from '~/types/sign-up'
import { getBrowserTimezone, getCountryCallingCodeFromAlpha2Code } from '~/utils/helpers'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'

import useHydrateRegisterFields from '../hooks/useHydrateRegisterFields'

export interface Context {
  backendError: string
  errors: RegisterErrors
  fields: RegisterFields
  page: Page
  setBackendError: Dispatch<SetStateAction<string>>
  setErrors: Dispatch<SetStateAction<RegisterErrors>>
  setFields: Dispatch<SetStateAction<RegisterFields>>
  setPage: Dispatch<SetStateAction<Page>>
  setValidPages: Dispatch<SetStateAction<Page[]>>
  signUpScenario: SignUpScenario
  validPages: Page[]
}

export const RegisterContext = createContext<Context | null>(null)

export function RegisterProvider({
  children,
  signUpScenario = SignUpScenario.DEFAULT,
}: {
  children: React.ReactElement
  signUpScenario?: SignUpScenario
}): React.ReactElement {
  const hydratedFields = useHydrateRegisterFields(signUpScenario)

  const [fields, setFields] = useState<RegisterFields>(() => hydratedFields)

  const [backendError, setBackendError] = useState('')
  const [errors, setErrors] = useState<RegisterErrors>({})

  const [validPages, setValidPages] = useState<Page[]>([])
  const { data, isLoading, isSuccess } = useGetUserLocationInfoFromIpQuery()

  useEffect(() => {
    if (!isSuccess && isLoading) return
    const countryCallingCode = getCountryCallingCodeFromAlpha2Code(data?.countryCode)

    setFields((prevState) => {
      const prevStateRef = { ...prevState }
      const state = {
        ...prevState,
        ...hydratedFields,
        wantsNewsletter: prevStateRef.wantsNewsletter,
        country: hydratedFields?.country || prevStateRef.country || data?.country,
        countryCallingCode: countryCallingCode || '+47',
        countryCode: data?.countryCode || 'NO',
        timezone: prevStateRef.timezone || getBrowserTimezone(),
        email: hydratedFields?.email || prevStateRef.email,
      }

      return state
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.country, data?.countryCode, hydratedFields.email, hydratedFields.authEmail])

  const context = {
    backendError,
    errors,
    fields,
    signUpScenario,
    validPages,
    setBackendError,
    setErrors,
    setFields,
    setValidPages,
  }

  return <RegisterContext.Provider value={context}>{children}</RegisterContext.Provider>
}
